import React from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import Icon, { ICON_NAMES } from 'components/ds/Icon'
import styles from './Feedback.module.scss'

const cx = (...p) => p.filter(Boolean).join(' ')

const TONE_CLASS = {
  success: styles.toneSuccess,
  error: styles.toneError,
  warning: styles.toneWarning,
  info: styles.toneInfo,
}

const TONE_GLYPH = { success: '✓', error: '!', warning: '!', info: 'i' }

const TONE_ICON = {
  watch: 'alert-triangle',
  warning: 'alert-triangle',
  error: 'alert-triangle',
  success: 'check-circle',
  info: 'info',
}

export const Toast = ({ tone = 'info', title, children, onClose, className, ...rest }) => (
  <div className={cx(styles.toast, TONE_CLASS[tone], className)} role="status" {...rest}>
    <span className={styles.toastIcon} aria-hidden="true">{TONE_GLYPH[tone]}</span>
    <div className={styles.toastBody}>
      {title && <span className={styles.toastTitle}>{title}</span>}
      {children && <span className={styles.toastText}>{children}</span>}
    </div>
    {onClose && (
      <button type="button" className={styles.close} onClick={onClose} aria-label="Dismiss">
        &times;
      </button>
    )}
  </div>
)

Toast.propTypes = {
  tone: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
  title: PropTypes.node,
  children: PropTypes.node,
  onClose: PropTypes.func,
  className: PropTypes.string,
}

export const Modal = ({ open, onClose, title, children, footer, labelledBy, className }) => {
  const dialogRef = React.useRef(null)
  const previouslyFocused = React.useRef(null)
  const titleId = React.useRef(`rox-modal-${Math.random().toString(36).slice(2, 9)}`).current

  React.useEffect(() => {
    if (!open) return undefined
    previouslyFocused.current = document.activeElement

    const node = dialogRef.current

    const focusables = () =>
      node
        ? Array.from(
            node.querySelectorAll(
              'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
          )
        : []

    const first = focusables()[0]
    if (first) first.focus()
    else if (node) node.focus()

    const onKeyDown = e => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key !== 'Tab') return
      const items = focusables()
      if (items.length === 0) return
      const firstEl = items[0]
      const lastEl = items[items.length - 1]
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault()
        lastEl.focus()
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault()
        firstEl.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)

      const prev = previouslyFocused.current
      if (prev && typeof prev.focus === 'function') prev.focus()
    }
  }, [open, onClose])

  if (!open) return null

  const dialog = (
    <div className={styles.scrim} onMouseDown={onClose}>
      <div
        ref={dialogRef}
        className={cx(styles.dialog, className)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy || (title ? titleId : undefined)}
        tabIndex={-1}
        onMouseDown={e => e.stopPropagation()}
      >
        {title && <h2 className={styles.dialogTitle} id={titleId}>{title}</h2>}
        <div className={styles.dialogBody}>{children}</div>
        {footer && <div className={styles.dialogFooter}>{footer}</div>}
      </div>
    </div>
  )

  return typeof document !== 'undefined' ? ReactDOM.createPortal(dialog, document.body) : dialog
}

Modal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  children: PropTypes.node,
  footer: PropTypes.node,
  labelledBy: PropTypes.string,
  className: PropTypes.string,
}

export const Tooltip = ({ label, placement = 'top', children, className }) => {
  const [open, setOpen] = React.useState(false)
  const id = React.useRef(`rox-tip-${Math.random().toString(36).slice(2, 9)}`).current

  return (
    <span
      className={cx(styles.tooltipWrap, className)}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {React.isValidElement(children)
        ? React.cloneElement(children, { 'aria-describedby': open ? id : undefined })
        : children}
      {open && (
        <span role="tooltip" id={id} className={cx(styles.tooltip, styles[placement])}>
          {label}
        </span>
      )}
    </span>
  )
}

Tooltip.propTypes = {
  label: PropTypes.node.isRequired,
  placement: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  children: PropTypes.node,
  className: PropTypes.string,
}

export const Banner = ({ tone = 'watch', icon, title, children, action, className, ...rest }) => {
  const iconName = icon === undefined ? TONE_ICON[tone] : icon
  return (
  <div
    className={cx(styles.banner, TONE_CLASS[tone === 'watch' ? 'warning' : tone], className)}
    role={tone === 'watch' ? 'alert' : 'status'}
    {...rest}
  >
    {iconName && <Icon name={iconName} size={18} className={styles.bannerIcon} />}
    <div className={styles.bannerBody}>
      {title && <span className={styles.bannerTitle}>{title}</span>}
      {children && <span className={styles.bannerText}>{children}</span>}
    </div>
    {action && <div className={styles.bannerAction}>{action}</div>}
  </div>
  )
}

Banner.propTypes = {
  tone: PropTypes.oneOf(['watch', 'success', 'error', 'info']),
  // defaults to the tone's icon; pass null for a banner that carries none
  icon: PropTypes.oneOf([...ICON_NAMES, null]),
  title: PropTypes.node,
  children: PropTypes.node,

  action: PropTypes.node,
  className: PropTypes.string,
}

export default Toast
